import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  ArrowRight, Calendar, MapPin, TrendingUp, Award, Users, 
  Zap, Wind, Sun, Battery, Search, Filter, Eye, ExternalLink,
  Building2, Star, CheckCircle, Target, BarChart3, Clock,
  IndianRupee, Sparkles, ChevronRight, Download, Shield,
  Grid, List, SlidersHorizontal, X
} from "lucide-react";
import { Link } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Footer from "../../components/layout/Footer";
import { useTheme } from "../../components/ThemeContext.tsx";

const CaseStudiesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('recent'); // 'recent', 'impact', 'alphabetical'
  const { isDarkMode } = useTheme();

  const categories = [
    { id: 'all', name: 'All Case Studies', count: 12 },
    { id: 'solar', name: 'Solar Energy', count: 5 },
    { id: 'wind', name: 'Wind Energy', count: 4 },
    { id: 'hybrid', name: 'Hybrid Systems', count: 2 },
    { id: 'storage', name: 'Energy Storage', count: 1 }
  ];

  const caseStudies = [
    {
      id: 1,
      title: "500MW Solar Farm Optimization",
      subtitle: "Rajasthan Solar Park",
      category: 'solar',
      client: "GreenTech Energy Solutions",
      location: "Rajasthan, India",
      date: "2024",
      duration: "18 months",
      image: "/assets/img/general/photovoltaic-system-2742302_1280.jpg",
      results: {
        efficiency: "+28%",
        downtime: "-45%",
        revenue: "+35%",
        cost: "-30%"
      },
      challenge: "A large-scale solar installation was experiencing significant performance gaps with actual generation 25% below projected output. Frequent equipment failures and lack of predictive maintenance were causing extensive downtime.",
      solution: "Implemented AxonMetric's AI-driven analytics platform with real-time monitoring, predictive maintenance algorithms, and automated performance optimization across all 500MW of capacity.",
      outcome: "Achieved 28% improvement in energy efficiency, reduced downtime by 45%, and increased overall revenue by 35% within the first year of implementation.",
      technologies: ['AI Analytics', 'Predictive Maintenance', 'Real-time Monitoring', 'Performance Optimization'],
      testimonial: {
        quote: "AxonMetric transformed our operations completely. The AI-driven insights helped us identify issues before they became problems, and the performance optimization exceeded our expectations.",
        author: "Sarah Chen",
        position: "CTO, GreenTech Energy Solutions"
      }
    },
    {
      id: 2,
      title: "Offshore Wind Farm Performance",
      subtitle: "North Sea Wind Project",
      category: 'wind',
      client: "WindFlow Corporation",
      location: "North Sea, UK",
      date: "2023",
      duration: "24 months",
      image: "/assets/img/general/windmills-1838788_1280.jpg",
      results: {
        efficiency: "+22%",
        downtime: "-60%",
        revenue: "+18%",
        cost: "-25%"
      },
      challenge: "Offshore wind turbines were experiencing frequent maintenance issues due to harsh marine conditions. Traditional maintenance schedules were inefficient and costly, leading to significant revenue losses.",
      solution: "Deployed comprehensive monitoring system with weather integration, vibration analysis, and predictive maintenance algorithms specifically designed for offshore wind environments.",
      outcome: "Reduced unplanned downtime by 60%, improved energy generation efficiency by 22%, and decreased maintenance costs by 25% while extending turbine lifespan.",
      technologies: ['Predictive Analytics', 'Weather Integration', 'Vibration Analysis', 'Remote Monitoring'],
      testimonial: {
        quote: "The predictive maintenance capabilities have been game-changing for our offshore operations. We can now predict failures weeks in advance.",
        author: "Michael Rodriguez",
        position: "Operations Director, WindFlow Corporation"
      }
    },
    {
      id: 3,
      title: "Hybrid Solar-Wind-Storage System",
      subtitle: "Smart Grid Integration",
      category: 'hybrid',
      client: "EcoPower Systems",
      location: "California, USA",
      date: "2024",
      duration: "12 months",
      image: "/assets/img/general/solar-2376233_1280.jpg",
      results: {
        efficiency: "+35%",
        downtime: "-50%",
        revenue: "+40%",
        cost: "-35%"
      },
      challenge: "Complex hybrid renewable energy system with solar, wind, and battery storage needed intelligent coordination to maximize efficiency and grid stability while minimizing costs.",
      solution: "Implemented advanced energy management system with machine learning algorithms to optimize energy dispatch, storage charging cycles, and grid interactions in real-time.",
      outcome: "Achieved 35% improvement in overall system efficiency, 40% increase in revenue through optimized energy trading, and established 24/7 renewable energy supply.",
      technologies: ['Energy Management', 'Machine Learning', 'Grid Integration', 'Battery Optimization'],
      testimonial: {
        quote: "The intelligent coordination between our solar, wind, and storage systems has maximized our ROI and made us a leader in renewable energy innovation.",
        author: "Emma Thompson",
        position: "Chief Sustainability Officer, EcoPower Systems"
      }
    },
    {
      id: 4,
      title: "Commercial Rooftop Solar Network",
      subtitle: "Urban Energy Optimization",
      category: 'solar',
      client: "Metro Solar Solutions",
      location: "Mumbai, India",
      date: "2023",
      duration: "15 months",
      image: "/assets/img/general/photovoltaic-4541308_1280.jpg",
      results: {
        efficiency: "+30%",
        downtime: "-40%",
        revenue: "+25%",
        cost: "-20%"
      },
      challenge: "Network of 50+ commercial rooftop solar installations across the city lacked centralized monitoring and optimization, leading to suboptimal performance and maintenance inefficiencies.",
      solution: "Deployed cloud-based monitoring platform with automated performance tracking, centralized maintenance scheduling, and energy production optimization across the entire network.",
      outcome: "Improved network-wide efficiency by 30%, reduced maintenance costs by 20%, and established a scalable model for urban solar deployment.",
      technologies: ['Cloud Monitoring', 'Network Analytics', 'Automated Scheduling', 'Urban Optimization'],
      testimonial: {
        quote: "Managing our distributed solar network became effortless with AxonMetric's platform. We can now monitor and optimize all 50+ sites from a single dashboard.",
        author: "Raj Patel",
        position: "CEO, Metro Solar Solutions"
      }
    },
    {
      id: 5,
      title: "Large-Scale Battery Storage",
      subtitle: "Grid Stabilization Project",
      category: 'storage',
      client: "PowerGrid Energy",
      location: "Texas, USA",
      date: "2024",
      duration: "8 months",
      image: "/assets/img/general/solar-cells-4518932_1280.jpg",
      results: {
        efficiency: "+25%",
        downtime: "-30%",
        revenue: "+45%",
        cost: "-15%"
      },
      challenge: "Massive 100MWh battery storage facility needed intelligent charge/discharge optimization to maximize grid services revenue while maintaining battery health and longevity.",
      solution: "Implemented advanced battery management system with grid signal integration, degradation modeling, and revenue optimization algorithms for multiple grid service markets.",
      outcome: "Increased revenue by 45% through optimized grid services participation, extended battery life by 25%, and improved grid stability for the region.",
      technologies: ['Battery Management', 'Grid Services', 'Revenue Optimization', 'Degradation Modeling'],
      testimonial: {
        quote: "Our battery storage facility is now generating 45% more revenue while actually improving battery longevity. The ROI exceeded all expectations.",
        author: "David Kim",
        position: "VP Operations, PowerGrid Energy"
      }
    },
    {
      id: 6,
      title: "Remote Wind Farm Monitoring",
      subtitle: "Harsh Environment Deployment",
      category: 'wind',
      client: "Arctic Wind Energy",
      location: "Northern Canada",
      date: "2023",
      duration: "20 months",
      image: "/assets/img/general/wind-power-generation-8370867_1280.jpg",
      results: {
        efficiency: "+20%",
        downtime: "-55%",
        revenue: "+30%",
        cost: "-40%"
      },
      challenge: "Remote wind farm in harsh Arctic conditions required robust monitoring and maintenance solutions to minimize costly site visits and maximize uptime in extreme weather.",
      solution: "Deployed ruggedized sensor network with satellite communication, AI-powered ice detection, and extreme weather prediction algorithms for optimal turbine operation.",
      outcome: "Reduced site visits by 40%, improved uptime by 55% during harsh weather periods, and increased annual energy production by 20% through optimized operation strategies.",
      technologies: ['Satellite Communication', 'Ice Detection AI', 'Weather Prediction', 'Remote Diagnostics'],
      testimonial: {
        quote: "Operating in the Arctic requires exceptional reliability. AxonMetric's solution has made our remote operations profitable and sustainable.",
        author: "Lisa Johnson",
        position: "Site Manager, Arctic Wind Energy"
      }
    }
  ];

  const filteredCaseStudies = caseStudies.filter(study => {
    const matchesCategory = selectedCategory === 'all' || study.category === selectedCategory;
    const matchesSearch = study.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         study.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const stats = [
    { label: "Case Studies", value: "12+", icon: Award },
    { label: "Average ROI Increase", value: "+35%", icon: TrendingUp },
    { label: "Clients Served", value: "500+", icon: Users },
    { label: "Countries", value: "15+", icon: MapPin }
  ];

  return (
    <Layout header={6}>
      <div className={`min-h-screen overflow-x-hidden w-full ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`} style={{ maxWidth: '100vw', boxSizing: 'border-box' }}>
        {/* Hero Section - Enhanced */}
        <section className="relative pt-24 pb-20 overflow-hidden" style={{ 
          background: isDarkMode 
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' 
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' 
        }}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231C4D88' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>

          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
                  style={{ 
                    borderColor: '#1C4D88', 
                    backgroundColor: isDarkMode ? '#1C4D8820' : '#1C4D8810' 
                  }}
                >
                  <Sparkles size={16} style={{ color: '#1C4D88' }} />
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Success Stories
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className={`text-5xl lg:text-6xl font-bold mb-6 leading-tight ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  Transforming 
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    Renewable Energy
                  </span>
                  <br />
                  Operations
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className={`text-lg mb-8 leading-relaxed ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  Discover how AxonMetric has delivered measurable results for renewable energy operators worldwide. 
                  From optimizing 500MW solar farms to managing offshore wind installations, our solutions drive 
                  real impact.
                </motion.p>

                {/* Quick Stats */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="grid grid-cols-3 gap-6 mb-8"
                >
                  <div>
                    <div className="text-3xl font-bold mb-1" style={{ color: '#1C4D88' }}>
                      500+
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Clients Worldwide
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1" style={{ color: '#1C4D88' }}>
                      +35%
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Avg ROI Increase
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold mb-1" style={{ color: '#1C4D88' }}>
                      15+
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Countries
                    </div>
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="flex flex-wrap gap-4"
                >
                  <a
                    href="#case-studies"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    style={{ background: 'linear-gradient(to right, #1C4D88, #0084CF)' }}
                  >
                    <span>Explore Case Studies</span>
                    <ArrowRight size={18} />
                  </a>
                  <Link
                    to="/contact"
                    className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 transition-all duration-300 ${
                      isDarkMode 
                        ? 'border-gray-600 text-white hover:bg-slate-800' 
                        : 'border-gray-300 text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <span>Request a Demo</span>
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right - Image Grid */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative hidden lg:block"
              >
                <div className="grid grid-cols-2 gap-4">
                  {/* Large featured image */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="col-span-2 relative rounded-2xl overflow-hidden shadow-2xl group"
                  >
                    <img 
                      src="/assets/img/general/solar-energy-7882650_1280.jpg" 
                      alt="Solar Energy Project"
                      className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-6 left-6 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                        <span className="text-sm font-medium">Live Project</span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">500MW Solar Farm</h3>
                      <p className="text-sm opacity-90">+28% efficiency improvement</p>
                    </div>
                  </motion.div>

                  {/* Two smaller images */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="relative rounded-2xl overflow-hidden shadow-xl group"
                  >
                    <img 
                      src="/assets/img/general/wind-power-generation-8370867_1280.jpg" 
                      alt="Wind Energy"
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="text-sm font-semibold">Wind Farms</p>
                      <p className="text-xs opacity-90">-60% downtime</p>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="relative rounded-2xl overflow-hidden shadow-xl group"
                  >
                    <img 
                      src="/assets/img/general/photovoltaic-4541308_1280.jpg" 
                      alt="Hybrid Systems"
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="text-sm font-semibold">Hybrid Systems</p>
                      <p className="text-xs opacity-90">+40% revenue</p>
                    </div>
                  </motion.div>
                </div>

                {/* Floating Achievement Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className={`absolute -left-6 bottom-12 p-4 rounded-xl shadow-2xl backdrop-blur-sm ${
                    isDarkMode ? 'bg-slate-800/90' : 'bg-white/90'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#1C4D8820' }}>
                      <Award style={{ color: '#1C4D88' }} size={24} />
                    </div>
                    <div>
                      <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Industry Leader
                      </div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        12+ case studies
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Impact Overview Section - NEW */}
        <section className={`py-16 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Measurable Impact Across the Industry
              </h2>
              <p className={`text-lg max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Our solutions deliver consistent, quantifiable results across diverse renewable energy portfolios
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`text-center p-6 rounded-2xl border ${
                    isDarkMode 
                      ? 'bg-slate-700/50 border-slate-600' 
                      : 'bg-gradient-to-br from-blue-50 to-white border-blue-100'
                  } hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center" style={{ 
                    background: 'linear-gradient(135deg, #1C4D88, #0084CF)' 
                  }}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className={`text-3xl font-bold mb-2 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    {stat.value}
                  </div>
                  <div className={`text-sm font-medium ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className={`p-6 rounded-2xl ${
                  isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-green-50 to-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-500/20">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      2.5+ TWh
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Energy Optimized Annually
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className={`p-6 rounded-2xl ${
                  isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-purple-50 to-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-500/20">
                    <IndianRupee className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      ₹450M+
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Cost Savings Delivered
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className={`p-6 rounded-2xl ${
                  isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-orange-50 to-white'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-orange-500/20">
                    <Clock className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <div className={`text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      50K+ Hours
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Downtime Prevented
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Case Study */}
        <section className={`py-16 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4"
                style={{ 
                  borderColor: '#1C4D88', 
                  backgroundColor: isDarkMode ? '#1C4D8820' : '#1C4D8810' 
                }}>
                <Star size={16} style={{ color: '#1C4D88' }} fill="#1C4D88" />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Featured Success Story
                </span>
              </span>
              <h2 className={`text-3xl lg:text-4xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                500MW Solar Farm Optimization
              </h2>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className={`rounded-3xl overflow-hidden shadow-2xl border ${
                isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
              }`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src="/assets/img/general/photovoltaic-system-2742302_1280.jpg"
                    alt="500MW Solar Farm"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-6 left-6">
                    <span className="px-4 py-2 rounded-lg text-sm font-semibold text-white backdrop-blur-sm"
                      style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)' }}>
                      Solar Energy
                    </span>
                  </div>
                </div>

                {/* Content Side */}
                <div className="p-8 lg:p-10">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`px-3 py-1 rounded-lg text-sm ${
                      isDarkMode ? 'bg-slate-700 text-gray-300' : 'bg-blue-50 text-blue-700'
                    }`}>
                      Rajasthan, India
                    </div>
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      2024 • 18 months
                    </div>
                  </div>

                  <h3 className={`text-2xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    GreenTech Energy Solutions
                  </h3>
                  
                  <p className={`text-base mb-6 leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    A large-scale solar installation was experiencing significant performance gaps with actual generation 
                    25% below projected output. Frequent equipment failures and lack of predictive maintenance were causing 
                    extensive downtime.
                  </p>

                  {/* Key Results */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className={`p-4 rounded-xl ${
                      isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-green-50 to-emerald-50'
                    }`}>
                      <div className="text-3xl font-bold mb-1" style={{ color: '#10b981' }}>+28%</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Efficiency Gain</div>
                    </div>
                    <div className={`p-4 rounded-xl ${
                      isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-blue-50 to-cyan-50'
                    }`}>
                      <div className="text-3xl font-bold mb-1" style={{ color: '#0084CF' }}>-45%</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Downtime</div>
                    </div>
                    <div className={`p-4 rounded-xl ${
                      isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-purple-50 to-violet-50'
                    }`}>
                      <div className="text-3xl font-bold mb-1" style={{ color: '#8b5cf6' }}>+35%</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Revenue</div>
                    </div>
                    <div className={`p-4 rounded-xl ${
                      isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-orange-50 to-amber-50'
                    }`}>
                      <div className="text-3xl font-bold mb-1" style={{ color: '#f59e0b' }}>-30%</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Costs</div>
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className={`p-4 rounded-xl border-l-4 mb-6 ${
                    isDarkMode ? 'bg-slate-700/30 border-blue-500' : 'bg-blue-50/50 border-blue-600'
                  }`}>
                    <p className={`text-sm italic mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      "AxonMetric transformed our operations completely. The AI-driven insights helped us identify 
                      issues before they became problems."
                    </p>
                    <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      — Sarah Chen, CTO
                    </p>
                  </div>

                  <Link
                    to="/case-study/1"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)' }}
                  >
                    <span>Read Full Case Study</span>
                    <ChevronRight size={18} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Explore Our Success Stories - Enhanced Section - COMMENTED OUT FOR FUTURE USE */}
        {false && <section id="case-studies" className={`py-16 ${
          isDarkMode ? 'bg-slate-900' : 'bg-gradient-to-b from-gray-50 to-white'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6"
                style={{ 
                  borderColor: '#1C4D88', 
                  backgroundColor: isDarkMode ? '#1C4D8820' : '#1C4D8810' 
                }}>
                <Filter size={16} style={{ color: '#1C4D88' }} />
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Browse Case Studies
                </span>
              </div>
              
              <h2 className={`text-4xl lg:text-5xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Explore Our 
                <span className="bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"> Success Stories</span>
              </h2>
              <p className={`text-lg max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Discover how we've helped renewable energy companies worldwide achieve remarkable results. 
                Filter by industry, search by keyword, or explore all our transformative projects.
              </p>
            </motion.div>

            {/* Advanced Search and Filter Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`rounded-2xl p-6 shadow-lg border mb-8 ${
                isDarkMode 
                  ? 'bg-slate-800 border-slate-700' 
                  : 'bg-white border-gray-200'
              }`}
            >
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <Search className={`absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`} />
                  <input
                    type="text"
                    placeholder="Search by project name, client, location, or technology..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full pl-14 pr-12 py-4 rounded-xl border-2 text-base ${
                      isDarkMode 
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500' 
                        : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    } focus:ring-0 transition-all duration-300`}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className={`absolute right-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${
                        isDarkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'
                      }`}
                    >
                      <X size={18} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                    </button>
                  )}
                </div>
              </div>

              {/* Filters Row */}
              <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
                {/* Category Filters */}
                <div className="flex-1 w-full">
                  <div className="flex items-center gap-2 mb-3">
                    <SlidersHorizontal size={16} style={{ color: '#1C4D88' }} />
                    <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Filter by Industry
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 max-w-full overflow-x-auto pb-2">
                    {categories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`group px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300 ${
                          selectedCategory === category.id
                            ? 'text-white shadow-md transform scale-105'
                            : isDarkMode
                              ? 'bg-slate-700 text-gray-300 hover:bg-slate-600 border border-slate-600'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
                        }`}
                        style={selectedCategory === category.id ? { background: 'linear-gradient(135deg, #1C4D88, #0084CF)' } : {}}
                      >
                        <span className="flex items-center gap-2">
                          {category.id === 'solar' && <Sun size={14} />}
                          {category.id === 'wind' && <Wind size={14} />}
                          {category.id === 'storage' && <Battery size={14} />}
                          {category.id === 'hybrid' && <Zap size={14} />}
                          <span>{category.name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            selectedCategory === category.id
                              ? 'bg-white/25'
                              : isDarkMode
                                ? 'bg-slate-600'
                                : 'bg-white'
                          }`}>
                            {category.count}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* View Mode and Sort */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full xl:w-auto">
                  {/* Sort Dropdown */}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <label className={`text-sm font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Sort by:
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className={`flex-1 sm:flex-initial px-3 py-2 rounded-lg border text-sm font-medium ${
                        isDarkMode
                          ? 'bg-slate-700 border-slate-600 text-white'
                          : 'bg-white border-gray-200 text-gray-700'
                      } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    >
                      <option value="recent">Most Recent</option>
                      <option value="impact">Highest Impact</option>
                      <option value="alphabetical">A-Z</option>
                    </select>
                  </div>

                  {/* View Mode Toggle */}
                  <div className={`flex items-center gap-1 p-1 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600' : 'bg-gray-100 border-gray-200'
                  }`}>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'grid'
                          ? 'text-white shadow-md'
                          : isDarkMode
                            ? 'text-gray-400 hover:text-gray-300'
                            : 'text-gray-500 hover:text-gray-700'
                      }`}
                      style={viewMode === 'grid' ? { background: 'linear-gradient(135deg, #1C4D88, #0084CF)' } : {}}
                      title="Grid View"
                    >
                      <Grid size={18} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-all duration-300 ${
                        viewMode === 'list'
                          ? 'text-white shadow-md'
                          : isDarkMode
                            ? 'text-gray-400 hover:text-gray-300'
                            : 'text-gray-500 hover:text-gray-700'
                      }`}
                      style={viewMode === 'list' ? { background: 'linear-gradient(135deg, #1C4D88, #0084CF)' } : {}}
                      title="List View"
                    >
                      <List size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Results Summary Bar */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 p-4 rounded-xl ${
                isDarkMode ? 'bg-slate-800/50' : 'bg-blue-50/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)' }}>
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Showing <span style={{ color: '#1C4D88' }}>{filteredCaseStudies.length}</span> of {caseStudies.length} case studies
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
                    {selectedCategory !== 'all' 
                      ? `Filtered by ${categories.find(c => c.id === selectedCategory)?.name}` 
                      : 'All categories'}
                    {searchTerm && ` • Search: "${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm}"`}
                  </p>
                </div>
              </div>

              {/* Quick Reset */}
              {(selectedCategory !== 'all' || searchTerm) && (
                <button
                  onClick={() => {
                    setSelectedCategory('all');
                    setSearchTerm('');
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    isDarkMode
                      ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <X size={16} />
                  <span className="hidden sm:inline">Clear Filters</span>
                  <span className="sm:hidden">Clear</span>
                </button>
              )}
            </motion.div>
          </div>
        </section>}

        {/* Case Studies Grid */}
        <section className={`py-12 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'} w-full overflow-x-hidden`}>
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`w-full ${viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 auto-rows-fr' : 'flex flex-col'} gap-4 md:gap-5 lg:gap-6`}>
              {filteredCaseStudies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 hover:border-blue-500/50' 
                      : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                  style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
                >
                  {/* Image */}
                  <div className="relative h-48 sm:h-56 overflow-hidden w-full" style={{ maxWidth: '100%' }}>
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      style={{ maxWidth: '100%', display: 'block' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-lg text-xs font-semibold capitalize backdrop-blur-sm text-white"
                        style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)' }}>
                        {study.category}
                      </span>
                    </div>

                    {/* Client Info */}
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="flex items-start justify-between gap-2 text-white">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold mb-0.5 truncate">{study.client}</p>
                          <p className="text-xs opacity-90 flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{study.location}</span>
                          </p>
                        </div>
                        <div className={`px-2 py-0.5 rounded text-xs font-medium backdrop-blur-sm flex-shrink-0 ${
                          isDarkMode ? 'bg-white/10' : 'bg-black/20'
                        }`}>
                          {study.date}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 w-full" style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                    {/* Title */}
                    <div className="mb-3 w-full">
                      <h3 className={`text-lg font-bold mb-1 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2 ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        {study.title}
                      </h3>
                    </div>

                    {/* Results Grid - Compact */}
                    <div className={`w-full grid grid-cols-4 gap-2 mb-4 p-3 rounded-lg ${
                      isDarkMode ? 'bg-slate-700/50' : 'bg-gradient-to-br from-blue-50 to-purple-50'
                    }`} style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
                      <div className="text-center min-w-0">
                        <div className="text-base sm:text-lg font-bold mb-0.5" style={{ color: '#0084CF' }}>
                          {study.results.efficiency}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Efficiency
                        </div>
                      </div>
                      <div className="text-center min-w-0">
                        <div className="text-base sm:text-lg font-bold mb-0.5" style={{ color: '#0084CF' }}>
                          {study.results.downtime}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Downtime
                        </div>
                      </div>
                      <div className="text-center min-w-0">
                        <div className="text-base sm:text-lg font-bold mb-0.5" style={{ color: '#0084CF' }}>
                          {study.results.revenue}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Revenue
                        </div>
                      </div>
                      <div className="text-center min-w-0">
                        <div className="text-base sm:text-lg font-bold mb-0.5" style={{ color: '#0084CF' }}>
                          {study.results.cost}
                        </div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Cost
                        </div>
                      </div>
                    </div>

                    {/* CTA Button - Single */}
                    <Link
                      to={`/case-study/${study.id}`}
                      className="w-full inline-flex items-center justify-center gap-2 text-white px-4 py-2.5 rounded-lg font-medium transition-all duration-300 hover:shadow-lg text-sm"
                      style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)', minWidth: 0 }}
                    >
                      <span>View Details</span>
                      <ArrowRight className="w-4 h-4 flex-shrink-0" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>

            {filteredCaseStudies.length === 0 && (
              <div className="text-center py-12">
                <div className={`text-6xl mb-4 ${
                  isDarkMode ? 'text-gray-600' : 'text-gray-300'
                }`}>
                  🔍
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  No case studies found
                </h3>
                <p className={`${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Try adjusting your search or filter criteria
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Client Testimonials Carousel - NEW - COMMENTED OUT FOR FUTURE USE */}
        {false && <section className={`py-20 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className={`text-3xl lg:text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                What Our Clients Say
              </h2>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Real feedback from renewable energy leaders worldwide
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  quote: "AxonMetric transformed our operations completely. The AI-driven insights helped us identify issues before they became problems.",
                  author: "Sarah Chen",
                  position: "CTO, GreenTech Energy",
                  image: "/assets/img/general/solar-cells-4518932_1280.jpg"
                },
                {
                  quote: "The predictive maintenance capabilities have been game-changing for our offshore operations. We can now predict failures weeks in advance.",
                  author: "Michael Rodriguez",
                  position: "Operations Director, WindFlow Corp",
                  image: "/assets/img/general/windmills-1838788_1280.jpg"
                },
                {
                  quote: "Our battery storage facility is now generating 45% more revenue while actually improving battery longevity. The ROI exceeded all expectations.",
                  author: "David Kim",
                  position: "VP Operations, PowerGrid Energy",
                  image: "/assets/img/general/photovoltaic-4525178_1280.jpg"
                }
              ].map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`rounded-2xl overflow-hidden border ${
                    isDarkMode 
                      ? 'bg-slate-700/50 border-slate-600' 
                      : 'bg-gradient-to-br from-blue-50 to-white border-gray-200'
                  } hover:shadow-xl transition-all duration-300`}
                >
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.author}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60"></div>
                  </div>
                  <div className="p-6 -mt-8 relative z-10">
                    <div className="mb-4">
                      <Star size={24} style={{ color: '#1C4D88' }} fill="#1C4D88" />
                    </div>
                    <p className={`text-sm italic mb-4 leading-relaxed ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #1C4D88, #0084CF)' }}>
                        <span className="text-white font-bold text-sm">
                          {testimonial.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {testimonial.author}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {testimonial.position}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>}

        {/* CTA Section - Enhanced */}
        <section className="relative py-24 overflow-hidden" style={{ 
          background: 'linear-gradient(135deg, #1C4D88 0%, #0084CF 100%)' 
        }}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>

          <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6">
                  <Sparkles size={16} className="text-white" />
                  <span className="text-sm font-semibold text-white">Join 500+ Clients</span>
                </div>

                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Ready to Write Your Success Story?
                </h2>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Join hundreds of renewable energy operators who have transformed their operations 
                  with AxonMetric's platform. Start your journey to improved efficiency and profitability today.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 bg-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:shadow-2xl transform hover:scale-105"
                    style={{ color: '#1C4D88' }}
                  >
                    <span>Start Your Journey</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white transition-all duration-300"
                    style={{ borderColor: 'white' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#1C4D88';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span>View Services</span>
                  </Link>
                </div>

                {/* Trust Indicators */}
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span className="text-white/90 text-sm">No credit card required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span className="text-white/90 text-sm">24/7 Support</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-300" />
                    <span className="text-white/90 text-sm">ROI Guarantee</span>
                  </div>
                </div>
              </motion.div>

              {/* Right Features */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-4"
              >
                {[
                  {
                    icon: TrendingUp,
                    title: "Proven Results",
                    description: "Average 35% ROI increase across all clients"
                  },
                  {
                    icon: Shield,
                    title: "Enterprise Security",
                    description: "Bank-level encryption and data protection"
                  },
                  {
                    icon: Users,
                    title: "Expert Support",
                    description: "Dedicated team of renewable energy specialists"
                  },
                  {
                    icon: BarChart3,
                    title: "Real-Time Analytics",
                    description: "Instant insights and actionable recommendations"
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20">
                      <feature.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">{feature.title}</h4>
                      <p className="text-sm text-white/80">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      <Footer footer={6} />
    </Layout>
  );
};

export default CaseStudiesPage;
